import { useEffect, useRef, useState } from 'react'

import { useCalculateDifferenceDistributionMutation } from '~/api/graphql/generated/types'
import {
  getExtendedPrice,
  getHighValue,
} from '~/components/dashboard/invoices/invoiceUtils'
import type { Bundle as BundleType } from '~/types'

interface UseBundleUpdateOptions {
  initialBundle?: BundleType
  shouldUseSetBundlePrice: boolean
}

export function useBundleUpdate({
  shouldUseSetBundlePrice,
  initialBundle = {} as BundleType,
}: UseBundleUpdateOptions) {
  const [bundleCandidate, setBundleCandidate] =
    useState<BundleType>(initialBundle)
  const currentController = useRef<AbortController>()

  useEffect(
    () => () => {
      currentController.current?.abort()
    },
    [],
  )

  const [
    calculateDifferenceDistribution,
    {
      loading: isDifferenceDistributionLoading,
      reset: resetDifferenceDistribution,
    },
  ] = useCalculateDifferenceDistributionMutation()

  const handleUpdateBundleCandidate = (
    newBundle: BundleType,
    shouldFetchDifferenceDistribution: boolean = true,
    onUpdate?: () => void,
  ) => {
    const abortController = new AbortController()
    currentController.current?.abort()
    currentController.current = abortController

    const fetchDifferenceDistribution =
      shouldUseSetBundlePrice &&
      newBundle.fixedPrice &&
      shouldFetchDifferenceDistribution &&
      newBundle.items?.length

    if (fetchDifferenceDistribution) {
      calculateDifferenceDistribution({
        variables: {
          values: newBundle.items?.map((item) =>
            getHighValue(getExtendedPrice(item)),
          ),
          fixedSum: newBundle.fixedPrice,
        },
        context: {
          fetchOptions: {
            signal: abortController.signal,
          },
        },
        onCompleted: (result) => {
          const updatedBundle = {
            ...newBundle,
            items: newBundle.items?.map((item, index) => ({
              ...item,
              discountAmount: result.calculateDifferenceDistribution[index],
              discountPerc: null,
            })),
          }
          setBundleCandidate(updatedBundle)
          onUpdate?.()
        },
      })
    } else {
      setBundleCandidate(newBundle)
      onUpdate?.()
    }
  }

  return {
    bundleCandidate,
    setBundleCandidate,
    handleUpdateBundleCandidate,
    isDifferenceDistributionLoading,
    resetDifferenceDistribution,
  }
}
